import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import {
  X,
  Truck,
  Package,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertCircle,
  Printer,
  Hash,
  XCircle,
  Ban,
  BarChart2,
  Archive,
} from "lucide-react";
import { domain } from "../../../../security";
import { DeliveryOrderPrint } from "../../DeliveryOrders/DeliveryOrdersModule/DeliveryOrderPrint";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
  }).format(val || 0);

// ─────────────────────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────────────────────

const DOStatusBadge = ({ status, isVoid }) => {
  if (isVoid) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-red-50 text-red-600 border-red-200">
        <Ban size={9} /> Voided
      </span>
    );
  }
  const s = (status || "").toLowerCase();
  const map = {
    sold: {
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      icon: <CheckCircle size={9} />,
    },
    delivered: {
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      icon: <CheckCircle size={9} />,
    },
    partial: {
      cls: "bg-amber-50 text-amber-700 border-amber-200",
      icon: <Clock size={9} />,
    },
    pending: {
      cls: "bg-blue-50 text-blue-700 border-blue-200",
      icon: <Clock size={9} />,
    },
  };
  const def = map[s] || {
    cls: "bg-gray-50 text-gray-600 border-gray-200",
    icon: <AlertCircle size={9} />,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${def.cls}`}
    >
      {def.icon} {status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────
// STACKED PROGRESS BAR
// ordered  = total ordered
// delivered = qty delivered (non-voided DOs)
// rejected  = qty rejected on SO line
// Each segment shown as % of ordered
// ─────────────────────────────────────────────────────────────

const StackedBar = ({ ordered, delivered, rejected }) => {
  if (!ordered || ordered <= 0) return null;

  const delPct = Math.min((delivered / ordered) * 100, 100);
  const rejPct = Math.min((rejected / ordered) * 100, 100 - delPct);
  const remPct = Math.max(100 - delPct - rejPct, 0);
  const effective = Math.max(ordered - rejected, 0);
  const remaining = Math.max(effective - delivered, 0);
  const isDone = remaining <= 0 && rejected < ordered;
  const isFullRej = rejected >= ordered;

  return (
    <div className="space-y-1.5">
      {/* Labels */}
      <div className="flex items-center justify-between text-[10px]">
        <span
          className={`font-bold ${isFullRej ? "text-red-500" : isDone ? "text-emerald-600" : "text-gray-700"}`}
        >
          {delivered} / {effective} delivered
          {rejected > 0 && (
            <span className="ml-1 text-red-400 font-normal">
              (+{rejected} rej.)
            </span>
          )}
        </span>
        {remaining > 0 ? (
          <span className="text-amber-600 font-semibold">{remaining} left</span>
        ) : isFullRej ? (
          <span className="text-red-500 font-bold flex items-center gap-0.5">
            <Ban size={9} /> All rejected
          </span>
        ) : (
          <span className="text-emerald-600 font-bold flex items-center gap-0.5">
            <CheckCircle size={9} /> Done
          </span>
        )}
      </div>

      {/* Bar */}
      <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
        {/* Delivered segment */}
        {delPct > 0 && (
          <div
            className={`h-full transition-all ${isDone ? "bg-emerald-500" : "bg-blue-500"} rounded-l-full`}
            style={{ width: `${delPct}%` }}
          />
        )}
        {/* Rejected segment */}
        {rejPct > 0 && (
          <div
            className="h-full bg-red-400 transition-all"
            style={{ width: `${rejPct}%` }}
          />
        )}
        {/* Remaining segment (already transparent from bg-gray-100) */}
      </div>

      {/* Legend */}
      {(delivered > 0 || rejected > 0) && (
        <div className="flex items-center gap-3 text-[9px] text-gray-400 flex-wrap">
          {delivered > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
              {delivered} delivered
            </span>
          )}
          {rejected > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
              {rejected} rejected
            </span>
          )}
          {remaining > 0 && (
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-200 inline-block" />
              {remaining} pending
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// OVERVIEW STAT PILL
// ─────────────────────────────────────────────────────────────

const StatPill = ({ value, label, colorClass, bgClass, icon }) => (
  <div
    className={`flex flex-col items-center justify-center rounded-2xl p-4 ${bgClass} gap-1`}
  >
    <div
      className={`flex items-center gap-1 text-2xl font-black ${colorClass}`}
    >
      {icon && <span className="opacity-70">{icon}</span>}
      {value}
    </div>
    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
      {label}
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────

const SODeliveryOrdersModal = ({ isOpen, onClose, soId, soNumber }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDO, setExpandedDO] = useState(null);
  const [activeSection, setActiveSection] = useState("overview"); // "overview" | "dos"

  const printRef = useRef(null);
  const [printTarget, setPrintTarget] = useState(null);
  const handlePrint = useReactToPrint({ content: () => printRef.current });
  const triggerPrint = (doItem) => {
    setPrintTarget(doItem);
    setTimeout(() => handlePrint(), 200);
  };

  const fetchData = useCallback(async () => {
    if (!soId || !isOpen) return;
    setLoading(true);
    try {
      const res = await axios.get(`${domain}/api/DeliveryOrders/bySo/${soId}`);
      setData(res.data);
    } catch {
      toast.error("Failed to load Delivery Orders for this SO.");
    } finally {
      setLoading(false);
    }
  }, [soId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setData(null);
      setExpandedDO(null);
      setActiveSection("overview");
      fetchData();
    }
  }, [isOpen, fetchData]);

  if (!isOpen) return null;

  const deliveryOrders = data?.deliveryOrders || [];
  const lineProgress = data?.lineProgress || [];
  const activeOrders = deliveryOrders.filter((d) => !d.isVoid);
  const voidedOrders = deliveryOrders.filter((d) => d.isVoid);

  // ── Summary totals from lineProgress ──────────────────────────────────────
  // NOTE: lineProgress[].rejectedQuantity is now exposed after the backend fix.
  const totalOrdered = lineProgress.reduce(
    (s, l) => s + (l.orderedQuantity || 0),
    0,
  );
  const totalDelivered = lineProgress.reduce(
    (s, l) => s + (l.deliveredQuantity || 0),
    0,
  );
  const totalRejected = lineProgress.reduce(
    (s, l) => s + (l.rejectedQuantity || 0),
    0,
  );
  const totalRemaining = lineProgress.reduce(
    (s, l) => s + (l.remainingQuantity || 0),
    0,
  );
  const totalValue = activeOrders.reduce((s, d) => s + (d.totalAmount || 0), 0);

  const effectiveOrdered = Math.max(totalOrdered - totalRejected, 0);
  const overallPct =
    effectiveOrdered > 0
      ? Math.min(Math.round((totalDelivered / effectiveOrdered) * 100), 100)
      : totalOrdered === 0
        ? 0
        : 100;

  const hasRejections = totalRejected > 0;

  // Status colour for circular indicator
  const isComplete = totalRemaining <= 0 && totalOrdered > 0;
  const ringColor = isComplete
    ? "text-emerald-500"
    : hasRejections
      ? "text-red-400"
      : "text-blue-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-3">
      {/* Hidden print target */}
      <div className="hidden">
        {printTarget && (
          <DeliveryOrderPrint ref={printRef} data={printTarget} />
        )}
      </div>

      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[93vh] flex flex-col overflow-hidden border border-gray-100">
        {/* ── TOP HEADER ── */}
        <div className="bg-indigo-600 px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center">
              <Truck size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base leading-tight">
                Delivery Tracker
              </h2>
              <p className="text-slate-400 text-xs mt-0.5 font-mono">
                {soNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition"
          >
            <X size={16} />
          </button>
        </div>

        {/* ── SECTION TABS ── */}
        <div className="flex items-center gap-1 px-5 pt-4 pb-0 bg-white border-b border-gray-100 shrink-0">
          {[
            {
              id: "overview",
              label: "Overview",
              icon: <BarChart2 size={13} />,
            },
            {
              id: "dos",
              label: `Delivery Orders (${deliveryOrders.length})`,
              icon: <Truck size={13} />,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition -mb-px ${
                activeSection === tab.id
                  ? "border-slate-800 text-slate-800"
                  : "border-transparent text-gray-400 hover:text-gray-700"
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* ── BODY ── */}
        <div className="flex-1 overflow-y-auto bg-gray-50/60">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-8 h-8 border-2 border-slate-800 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : /* ══════════════ OVERVIEW TAB ══════════════ */
          activeSection === "overview" ? (
            <div className="p-5 space-y-4">
              {/* ── BIG PICTURE STATS ── */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">
                  Big Picture
                </p>

                {/* Circular % + stat pills */}
                <div className="flex items-center gap-5">
                  {/* Ring / percentage */}
                  <div className="relative shrink-0 w-24 h-24">
                    <svg
                      viewBox="0 0 36 36"
                      className="w-full h-full -rotate-90"
                    >
                      {/* Background ring */}
                      <circle
                        cx="18"
                        cy="18"
                        r="15.9"
                        fill="none"
                        stroke="#f1f5f9"
                        strokeWidth="3.5"
                      />
                      {/* Rejected arc (red) */}
                      {hasRejections && (
                        <circle
                          cx="18"
                          cy="18"
                          r="15.9"
                          fill="none"
                          stroke="#f87171"
                          strokeWidth="3.5"
                          strokeDasharray={`${Math.min((totalRejected / totalOrdered) * 100, 100)} 100`}
                          strokeDashoffset={`-${Math.min((totalDelivered / totalOrdered) * 100, 100)}`}
                          strokeLinecap="round"
                        />
                      )}
                      {/* Delivered arc */}
                      <circle
                        cx="18"
                        cy="18"
                        r="15.9"
                        fill="none"
                        stroke={isComplete ? "#10b981" : "#3b82f6"}
                        strokeWidth="3.5"
                        strokeDasharray={`${Math.min((totalDelivered / totalOrdered) * 100, 100)} 100`}
                        strokeLinecap="round"
                      />
                    </svg>
                    {/* Centre text */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className={`text-lg font-black ${ringColor}`}>
                        {overallPct}%
                      </span>
                      <span className="text-[8px] text-gray-400 font-bold">
                        done
                      </span>
                    </div>
                  </div>

                  {/* Stat pills grid */}
                  <div
                    className={`flex-1 grid gap-2 ${hasRejections ? "grid-cols-4" : "grid-cols-3"}`}
                  >
                    <StatPill
                      value={totalOrdered}
                      label="Ordered"
                      colorClass="text-gray-700"
                      bgClass="bg-gray-50"
                    />
                    <StatPill
                      value={totalDelivered}
                      label="Delivered"
                      colorClass="text-emerald-600"
                      bgClass="bg-emerald-50"
                      icon={<CheckCircle size={14} />}
                    />
                    {hasRejections && (
                      <StatPill
                        value={totalRejected}
                        label="Rejected"
                        colorClass="text-red-500"
                        bgClass="bg-red-50"
                        icon={<Ban size={14} />}
                      />
                    )}
                    <StatPill
                      value={totalRemaining}
                      label="Remaining"
                      colorClass={
                        totalRemaining === 0
                          ? "text-emerald-600"
                          : "text-amber-600"
                      }
                      bgClass={
                        totalRemaining === 0 ? "bg-emerald-50" : "bg-amber-50"
                      }
                      icon={
                        totalRemaining === 0 ? (
                          <CheckCircle size={14} />
                        ) : (
                          <Clock size={14} />
                        )
                      }
                    />
                  </div>
                </div>

                {/* Value delivered */}
                {totalValue > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                    <span className="text-gray-400 font-medium text-xs">
                      Total Value Delivered
                    </span>
                    <span className="font-black text-gray-900 text-base">
                      {formatCurrency(totalValue)}
                    </span>
                  </div>
                )}
              </div>

              {/* ── PER-LINE PROGRESS ── */}
              {lineProgress.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
                    <Package size={13} className="text-gray-400" />
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Line Item Breakdown
                    </h3>
                  </div>

                  <div className="divide-y divide-gray-50">
                    {lineProgress.map((line) => {
                      const rej = line.rejectedQuantity || 0;
                      const del = line.deliveredQuantity || 0;
                      const ord = line.orderedQuantity || 0;
                      const rem = line.remainingQuantity || 0;

                      // Line status indicator
                      const lineDone = rem <= 0 && rej < ord;
                      const lineFullRej = rej >= ord;
                      const lineStatusCls = lineFullRej
                        ? "border-l-red-400 bg-red-50/30"
                        : lineDone
                          ? "border-l-emerald-400 bg-emerald-50/20"
                          : rej > 0
                            ? "border-l-orange-300 bg-orange-50/20"
                            : "border-l-blue-200";

                      return (
                        <div
                          key={line.id}
                          className={`px-5 py-4 border-l-4 ${lineStatusCls}`}
                        >
                          {/* Product name + code + badges */}
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {line.productCode && (
                                  <span className="inline-flex items-center gap-0.5 text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded font-mono text-[10px] border border-indigo-100 shrink-0">
                                    <Hash size={8} />
                                    {line.productCode}
                                  </span>
                                )}
                                <span className="text-sm font-bold text-gray-800 truncate">
                                  {line.productName}
                                </span>
                              </div>
                            </div>

                            {/* Right-side mini badges */}
                            <div className="flex items-center gap-1 shrink-0">
                              {lineFullRej ? (
                                <span className="text-[9px] font-bold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                  <Ban size={8} /> All rejected
                                </span>
                              ) : lineDone ? (
                                <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                  <CheckCircle size={8} /> Complete
                                </span>
                              ) : rej > 0 ? (
                                <span className="text-[9px] font-bold text-orange-600 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                                  <XCircle size={8} /> {rej} rejected
                                </span>
                              ) : null}
                            </div>
                          </div>

                          {/* Stacked progress bar */}
                          <StackedBar
                            ordered={ord}
                            delivered={del}
                            rejected={rej}
                          />

                          {/* Qty breakdown chips */}
                          <div className="flex items-center gap-2 flex-wrap mt-2.5">
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">
                              Ordered: {ord}
                            </span>
                            <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-semibold">
                              Delivered: {del}
                            </span>
                            {rej > 0 && (
                              <span className="text-[10px] bg-red-50 text-red-500 px-2 py-0.5 rounded-full font-semibold flex items-center gap-0.5">
                                <Ban size={8} /> Rejected: {rej}
                              </span>
                            )}
                            {rem > 0 && (
                              <span className="text-[10px] bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full font-semibold">
                                Remaining: {rem}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {lineProgress.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center py-14 text-center bg-white rounded-2xl border border-gray-200">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <Package size={20} className="text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-semibold text-sm">
                    No delivery data yet
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Create a Delivery Order to see progress.
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* ══════════════ DELIVERY ORDERS TAB ══════════════ */
            <div className="p-5 space-y-3">
              {deliveryOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-200">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <Truck size={20} className="text-gray-300" />
                  </div>
                  <p className="text-gray-600 font-semibold text-sm">
                    No Delivery Orders yet
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Create one from the Delivery Orders screen.
                  </p>
                </div>
              ) : (
                <>
                  {/* Active DOs */}
                  {activeOrders.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">
                        Active ({activeOrders.length})
                      </p>
                      {activeOrders.map((doItem) => (
                        <DOCard
                          key={doItem.id}
                          doItem={doItem}
                          isExpanded={expandedDO === doItem.id}
                          onToggle={() =>
                            setExpandedDO(
                              expandedDO === doItem.id ? null : doItem.id,
                            )
                          }
                          onPrint={() => triggerPrint(doItem)}
                        />
                      ))}
                    </div>
                  )}

                  {/* Voided DOs */}
                  {voidedOrders.length > 0 && (
                    <div className="space-y-2 mt-3">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1 flex items-center gap-1">
                        <Archive size={10} /> Voided ({voidedOrders.length})
                      </p>
                      {voidedOrders.map((doItem) => (
                        <DOCard
                          key={doItem.id}
                          doItem={doItem}
                          isExpanded={expandedDO === doItem.id}
                          onToggle={() =>
                            setExpandedDO(
                              expandedDO === doItem.id ? null : doItem.id,
                            )
                          }
                          onPrint={() => triggerPrint(doItem)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="px-6 py-3.5 border-t border-gray-100 flex items-center justify-between bg-white shrink-0">
          <p className="text-xs text-gray-400">
            {activeOrders.length} active · {voidedOrders.length} voided
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-200 text-gray-600 rounded-xl text-xs font-semibold hover:bg-gray-50 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// DO CARD (used in the "Delivery Orders" tab)
// ─────────────────────────────────────────────────────────────

const DOCard = ({ doItem, isExpanded, onToggle, onPrint }) => {
  const items = doItem.items || [];

  return (
    <div
      className={`bg-white rounded-xl border shadow-sm overflow-hidden transition ${
        doItem.isVoid ? "border-gray-100 opacity-60" : "border-gray-200"
      }`}
    >
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50/80 transition"
        onClick={onToggle}
      >
        <button className="p-1 text-gray-300 hover:text-gray-500 rounded-lg shrink-0">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-900 text-sm font-mono">
              {doItem.deliveryOrderNumber}
            </span>
            <DOStatusBadge status={doItem.status} isVoid={doItem.isVoid} />
          </div>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {formatDate(doItem.deliveryDate || doItem.date)}
            {" · "}
            {items.length} line{items.length !== 1 ? "s" : ""}
            {" · "}
            <span className="font-semibold text-gray-600">
              {formatCurrency(doItem.totalAmount)}
            </span>
          </p>
        </div>

        {!doItem.isVoid && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrint();
            }}
            className="p-2 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition shrink-0"
            title="Print DO"
          >
            <Printer size={14} />
          </button>
        )}
      </div>

      {/* Expanded line items */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4">
          {items.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">
              No line items.
            </p>
          ) : (
            <table className="w-full text-xs text-left text-gray-600">
              <thead>
                <tr className="text-[10px] text-gray-400 uppercase font-bold">
                  <th className="pb-2 pr-4">Item</th>
                  <th className="pb-2 text-center">Qty / UOM</th>
                  <th className="pb-2 text-right">Unit Price</th>
                  <th className="pb-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-white transition">
                    <td className="py-2.5 pr-4">
                      <p className="font-semibold text-gray-900 leading-tight">
                        {item.productName}
                      </p>
                      {/* itemCode — enriched by backend */}
                      {item.itemCode && (
                        <span className="inline-flex items-center gap-0.5 text-indigo-500 font-mono text-[10px] mt-0.5">
                          <Hash size={8} />
                          {item.itemCode}
                        </span>
                      )}
                      {item.serials && item.serials.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.serials.map((s, idx) => (
                            <span
                              key={idx}
                              className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono"
                            >
                              {s.serialCode}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>

                    <td className="py-2.5 text-center">
                      <span className="font-bold text-emerald-700">
                        {/* quantityDelivered — C# projection field name for GetBySo */}
                        {item.quantityDelivered ?? 0}
                      </span>
                      {/* unitName — enriched by backend (UnitOfMeasurements.UnitName) */}
                      {(item.unitName || item.uomName) && (
                        <span className="ml-1 text-gray-400 font-normal">
                          {item.unitName || item.uomName}
                        </span>
                      )}
                    </td>

                    <td className="py-2.5 text-right text-gray-500">
                      {formatCurrency(item.unitPrice)}
                    </td>

                    <td className="py-2.5 text-right font-bold text-gray-900">
                      {formatCurrency(item.lineTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200">
                  <td
                    colSpan="3"
                    className="pt-2.5 text-right font-bold text-gray-500 text-xs"
                  >
                    Total:
                  </td>
                  <td className="pt-2.5 text-right font-black text-gray-900">
                    {formatCurrency(doItem.totalAmount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default SODeliveryOrdersModal;
